import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import "./lastProjects/LastProjects.css";
import "./DashboardHome.css";

const QUICK_LINK_SECTIONS = [
  {
    title: "Store",
    icon: "uil uil-store",
    links: [
      { to: "/dashboard/store/products", icon: "uil uil-package", label: "Store Products" },
      { to: "/dashboard/store/add-product", icon: "fa-solid fa-plus", label: "Add Product" },
      { to: "/dashboard/orders", icon: "uil uil-shopping-cart", label: "Orders" },
      { to: "/dashboard/store/product-reviews", icon: "uil uil-star", label: "Customer Reviews" },
    ],
  },
  {
    title: "Projects",
    icon: "uil uil-presentation-edit",
    links: [
      { to: "/dashboard/add-project", icon: "fa-solid fa-plus", label: "Add Project" },
      { to: "/dashboard/projects", icon: "uil uil-chart", label: "Projects" },
    ],
  },
  {
    title: "Communications",
    icon: "uil uil-chat-bubble-user",
    links: [
      { to: "/dashboard/messages", icon: "uil uil-message", label: "Messages" },
      { to: "/dashboard/jobs", icon: "uil uil-briefcase-alt", label: "Jobs" },
    ],
  },
  {
    title: "Reviews",
    icon: "uil uil-star",
    links: [
      { to: "/dashboard/add-review", icon: "fa-solid fa-plus", label: "Add Review" },
      { to: "/dashboard/reviews", icon: "uil uil-star", label: "Reviews" },
    ],
  },
  {
    title: "Partners",
    icon: "uil uil-users-alt",
    links: [
      { to: "/dashboard/add-partner", icon: "fa-solid fa-plus", label: "Add Partner" },
    ],
  },
  {
    title: "Content",
    icon: "uil uil-sliders-v-alt",
    links: [
      { to: "/dashboard/content/home", icon: "fa-solid fa-sliders", label: "Home" },
      { to: "/dashboard/content/about", icon: "fa-solid fa-sliders", label: "About" },
      { to: "/dashboard/content/contact-join", icon: "fa-solid fa-sliders", label: "Contact & Join" },
      { to: "/dashboard/store/policies", icon: "uil uil-file-alt", label: "Policies" },
    ],
  },
];

const TRAFFIC_SOURCE_META = {
  instagram:  { label: "Instagram",   icon: "fab fa-instagram",  color: "#E1306C" },
  facebook:   { label: "Facebook",    icon: "fab fa-facebook",   color: "#1877F2" },
  whatsapp:   { label: "WhatsApp",    icon: "fab fa-whatsapp",   color: "#25D366" },
  google:     { label: "Google",      icon: "fab fa-google",     color: "#EA4335" },
  twitter:    { label: "X / Twitter", icon: "fab fa-x-twitter",  color: "#334155" },
  tiktok:     { label: "TikTok",      icon: "fab fa-tiktok",     color: "#FF004F" },
  snapchat:   { label: "Snapchat",    icon: "fab fa-snapchat",   color: "#F59E0B" },
  youtube:    { label: "YouTube",     icon: "fab fa-youtube",    color: "#FF0000" },
  linkedin:   { label: "LinkedIn",    icon: "fab fa-linkedin",   color: "#0A66C2" },
  direct:     { label: "Direct",      icon: "uil uil-link",      color: "#6366F1" },
  other:      { label: "Other",       icon: "uil uil-globe",     color: "#64748B" },
};

const ORDER_STATUS_META = {
  pending:   { label: "Pending",   color: "#F59E0B", bg: "#FFFBEB" },
  confirmed: { label: "Confirmed", color: "#3B82F6", bg: "#EFF6FF" },
  shipped:   { label: "Shipped",   color: "#8B5CF6", bg: "#F5F3FF" },
  delivered: { label: "Delivered", color: "#10B981", bg: "#F0FDF4" },
  cancelled: { label: "Cancelled", color: "#EF4444", bg: "#FEF2F2" },
};

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diffMs = Date.now() - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

function TrendBadge({ value }) {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  return (
    <span className={`dash-trend-badge ${up ? "dash-trend-badge--up" : "dash-trend-badge--down"}`}>
      <i className={`uil ${up ? "uil-arrow-up" : "uil-arrow-down"}`} />
      {Math.abs(value)}%
    </span>
  );
}

function OrderStatusBadge({ status }) {
  const meta = ORDER_STATUS_META[status] || ORDER_STATUS_META.pending;
  return (
    <span
      className="dash-order-status-badge"
      style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}22` }}
    >
      {meta.label}
    </span>
  );
}

function DashboardHome() {
  const [adminName, setAdminName] = useState("");
  const [dashStats, setDashStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [chartsData, setChartsData] = useState(null);
  const [chartsRange, setChartsRange] = useState("7d");
  const [chartsLoading, setChartsLoading] = useState(false);
  const [activeVisitors, setActiveVisitors] = useState(null);

  const todayLabel = new Intl.DateTimeFormat("en", {
    weekday: "long", month: "short", day: "numeric",
  }).format(new Date());

  const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");

  useEffect(() => {
    if (!token) return;

    axios.get("/admin/profile", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAdminName(res.data?.name || "Admin"))
      .catch(() => {});

    axios.get("analytics/dashboard-stats", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setDashStats(res.data))
      .catch(() => {});

    setAnalyticsLoading(true);
    axios.get("analytics/summary", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setAnalytics(res.data))
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));

    // charts fetched separately via chartsRange effect below

    const fetchActiveVisitors = () =>
      axios.get("analytics/active-visitors", { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setActiveVisitors(res.data.count ?? 0))
        .catch(() => {});
    fetchActiveVisitors();
    const visitorInterval = setInterval(fetchActiveVisitors, 30000);
    return () => clearInterval(visitorInterval);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setChartsLoading(true);
    axios.get(`analytics/charts?range=${chartsRange}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setChartsData(res.data))
      .catch(() => {})
      .finally(() => setChartsLoading(false));
  }, [token, chartsRange]);

  const needsAttention =
    dashStats && (dashStats.pendingOrders > 0 || dashStats.unreadMessages > 0);

  return (
    <main className="lastProjects dashboard-home">
      <div className="container dashboard-home-container">

        {/* Print-only header */}
        <div className="dashboard-home-print-header" aria-hidden="true">
          <div>
            <div className="dashboard-home-print-header-title">Alramlaa Wooden Products — Dashboard Report</div>
            <div className="dashboard-home-print-header-sub">الرملاء للصناعات الخشبية</div>
          </div>
          <div className="dashboard-home-print-header-date">
            Generated: {new Date().toLocaleDateString("en", { year: "numeric", month: "long", day: "numeric" })}
          </div>
        </div>

        {/* Header */}
        <header className="dashboard-home-header">
          <div className="dashboard-home-header-text">
            <h1>Dashboard</h1>
            <p className="dashboard-home-welcome">
              Welcome back{adminName ? `, ${adminName}` : ""}
            </p>
          </div>
          <div className="dashboard-home-header-meta">
            {activeVisitors !== null && (
              <span className="dashboard-home-live-pill">
                <span className="dashboard-home-live-dot" aria-hidden />
                {activeVisitors} online now
              </span>
            )}
            <span className="dashboard-home-date-pill">{todayLabel}</span>
            <button
              className="dashboard-home-print-btn"
              onClick={() => window.print()}
              title="Print analytics report as PDF"
            >
              <i className="uil uil-print" aria-hidden />
              Print Report
            </button>
          </div>
        </header>

        {/* Needs Attention Banner */}
        {needsAttention && (
          <div className="dash-attention-bar">
            <i className="uil uil-exclamation-triangle dash-attention-bar__icon" />
            <span className="dash-attention-bar__text">Needs your attention:</span>
            {dashStats.pendingOrders > 0 && (
              <Link to="/dashboard/orders?status=pending" className="dash-attention-bar__chip dash-attention-bar__chip--orders">
                <i className="uil uil-shopping-cart" />
                {dashStats.pendingOrders} pending order{dashStats.pendingOrders !== 1 ? "s" : ""}
              </Link>
            )}
            {dashStats.unreadMessages > 0 && (
              <Link to="/dashboard/messages" className="dash-attention-bar__chip dash-attention-bar__chip--messages">
                <i className="uil uil-envelope" />
                {dashStats.unreadMessages} unread message{dashStats.unreadMessages !== 1 ? "s" : ""}
              </Link>
            )}
          </div>
        )}

        {/* KPI Cards — 6 across */}
        <section className="dashboard-home-stats-section">
          <h2 className="dashboard-home-section-title">Overview</h2>
          <div className="dashboard-home-kpi-grid">

            {/* Product Views */}
            {analytics && (
              <div className="dashboard-home-kpi-card dashboard-home-kpi-card--blue">
                <div className="dashboard-home-kpi-icon"><i className="uil uil-eye" /></div>
                <div className="dashboard-home-kpi-body">
                  <span className="dashboard-home-kpi-value">{analytics.totalProductViews}</span>
                  <span className="dashboard-home-kpi-label">Product Views</span>
                </div>
              </div>
            )}

            {/* WhatsApp Clicks */}
            {analytics && (
              <div className="dashboard-home-kpi-card dashboard-home-kpi-card--green">
                <div className="dashboard-home-kpi-icon"><i className="fab fa-whatsapp" /></div>
                <div className="dashboard-home-kpi-body">
                  <span className="dashboard-home-kpi-value">{analytics.whatsappClicks}</span>
                  <span className="dashboard-home-kpi-label">WhatsApp Clicks</span>
                </div>
              </div>
            )}

            {/* Total Orders */}
            <Link to="/dashboard/orders" className="dashboard-home-kpi-card dashboard-home-kpi-card--amber">
              <div className="dashboard-home-kpi-icon"><i className="uil uil-shopping-cart" /></div>
              <div className="dashboard-home-kpi-body">
                <span className="dashboard-home-kpi-value">
                  {dashStats ? dashStats.totalOrders : (analytics?.totalOrders ?? "—")}
                </span>
                <span className="dashboard-home-kpi-label">Total Orders</span>
                {dashStats?.trends?.orders !== undefined && (
                  <TrendBadge value={dashStats.trends.orders} />
                )}
              </div>
              <i className="uil uil-arrow-right dashboard-home-kpi-arrow" />
            </Link>

            {/* Pending Orders */}
            <Link to="/dashboard/orders?status=pending" className="dashboard-home-kpi-card dashboard-home-kpi-card--orange">
              <div className="dashboard-home-kpi-icon"><i className="uil uil-clock" /></div>
              <div className="dashboard-home-kpi-body">
                <span className="dashboard-home-kpi-value">{dashStats ? dashStats.pendingOrders : "—"}</span>
                <span className="dashboard-home-kpi-label">Pending Orders</span>
              </div>
              <i className="uil uil-arrow-right dashboard-home-kpi-arrow" />
            </Link>

            {/* Unread Messages */}
            <Link to="/dashboard/messages" className="dashboard-home-kpi-card dashboard-home-kpi-card--teal">
              <div className="dashboard-home-kpi-icon"><i className="uil uil-envelope" /></div>
              <div className="dashboard-home-kpi-body">
                <span className="dashboard-home-kpi-value">{dashStats ? dashStats.unreadMessages : "—"}</span>
                <span className="dashboard-home-kpi-label">Unread Messages</span>
                {dashStats?.trends?.messages !== undefined && (
                  <TrendBadge value={dashStats.trends.messages} />
                )}
              </div>
              <i className="uil uil-arrow-right dashboard-home-kpi-arrow" />
            </Link>

            {/* Store Products */}
            <Link to="/dashboard/store/products" className="dashboard-home-kpi-card dashboard-home-kpi-card--purple">
              <div className="dashboard-home-kpi-icon"><i className="uil uil-package" /></div>
              <div className="dashboard-home-kpi-body">
                <span className="dashboard-home-kpi-value">{dashStats ? dashStats.totalProducts : "—"}</span>
                <span className="dashboard-home-kpi-label">Store Products</span>
              </div>
              <i className="uil uil-arrow-right dashboard-home-kpi-arrow" />
            </Link>

            {/* Conversion Rate */}
            {analytics && (
              <div className="dashboard-home-kpi-card dashboard-home-kpi-card--indigo">
                <div className="dashboard-home-kpi-icon"><i className="uil uil-percentage" /></div>
                <div className="dashboard-home-kpi-body">
                  <span className="dashboard-home-kpi-value">{analytics.conversionRate}%</span>
                  <span className="dashboard-home-kpi-label">Conversion Rate</span>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Charts */}
        <section className="dashboard-home-analytics-section">
          <h2 className="dashboard-home-section-title">Analytics</h2>
          {analyticsLoading ? (
            <div className="dashboard-home-analytics-loading">Loading analytics…</div>
          ) : analytics ? (
            <>
              {chartsData && (
                <div className="dashboard-home-charts-section">
                  <div className="dashboard-home-chart-card dashboard-home-chart-card--wide">
                    <div className="dash-chart-header">
                      <h3 className="dashboard-home-analytics-card-title">
                        <i className="uil uil-chart-line" />
                        Views vs Orders
                      </h3>
                      <div className="dash-range-pills">
                        {[
                          { key: "7d",  label: "7 Days"  },
                          { key: "30d", label: "30 Days" },
                          { key: "3m",  label: "3 Months"},
                          { key: "1y",  label: "1 Year"  },
                        ].map(({ key, label }) => (
                          <button
                            key={key}
                            type="button"
                            className={`dash-range-pill${chartsRange === key ? " is-active" : ""}`}
                            onClick={() => setChartsRange(key)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className={`dash-chart-wrap${chartsLoading ? " is-loading" : ""}`}>
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={chartsData.timeSeries} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
                        <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="views" name="Views" stroke="#3B82F6" fill="url(#gradViews)" strokeWidth={2.5} dot={{ r: 4, fill: "#3B82F6" }} />
                        <Area type="monotone" dataKey="orders" name="Orders" stroke="#10B981" fill="url(#gradOrders)" strokeWidth={2.5} dot={{ r: 4, fill: "#10B981" }} />
                      </AreaChart>
                    </ResponsiveContainer>
                    {chartsLoading && <div className="dash-chart-loading"><span className="dash-chart-spinner" /></div>}
                    </div>
                  </div>

                  <div className="dashboard-home-charts-row">
                    {chartsData.trafficSources.length > 0 && (
                      <div className="dashboard-home-chart-card">
                        <h3 className="dashboard-home-analytics-card-title">
                          <i className="uil uil-arrow-growth" />
                          Traffic Sources
                        </h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={chartsData.trafficSources} dataKey="count" nameKey="_id" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                              {chartsData.trafficSources.map((entry) => {
                                const meta = TRAFFIC_SOURCE_META[entry._id] || TRAFFIC_SOURCE_META.other;
                                return <Cell key={entry._id} fill={meta.color} />;
                              })}
                            </Pie>
                            <Tooltip formatter={(value, name) => [value, (TRAFFIC_SOURCE_META[name] || TRAFFIC_SOURCE_META.other).label]} />
                            <Legend formatter={(value) => (TRAFFIC_SOURCE_META[value] || TRAFFIC_SOURCE_META.other).label} wrapperStyle={{ fontSize: 11 }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {chartsData.topProducts.length > 0 && (
                      <div className="dashboard-home-chart-card">
                        <h3 className="dashboard-home-analytics-card-title">
                          <i className="uil uil-fire" />
                          Top Products (Views)
                        </h3>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart layout="vertical" data={chartsData.topProducts.map((p) => ({ name: (p.productArName || p.productName || p._id).slice(0, 20), views: p.count }))} margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
                            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10, fill: "#6b7280" }} />
                            <Tooltip contentStyle={{ borderRadius: 10, fontSize: 13 }} />
                            <Bar dataKey="views" name="Views" radius={[0, 6, 6, 0]}>
                              {chartsData.topProducts.map((_, i) => (
                                <Cell key={i} fill={["#6366F1","#3B82F6","#10B981","#F59E0B","#EF4444"][i % 5]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Top Products + Top Categories */}
              <div className="dashboard-home-analytics-lists">
                <div className="dashboard-home-analytics-card">
                  <h3 className="dashboard-home-analytics-card-title">
                    <i className="uil uil-fire" />
                    Top Viewed Products
                  </h3>
                  <ul className="dashboard-home-analytics-list">
                    {(analytics.topProducts || []).length === 0 ? (
                      <li className="dashboard-home-analytics-empty">No data yet</li>
                    ) : (
                      (analytics.topProducts || []).map((p, i) => (
                        <li key={p._id} className="dashboard-home-analytics-product-row">
                          <span className="dashboard-home-analytics-product-rank">#{i + 1}</span>
                          {p.productImageUrl ? (
                            <img src={p.productImageUrl} alt={p.productName || ""} className="dashboard-home-analytics-product-thumb" />
                          ) : (
                            <span className="dashboard-home-analytics-product-thumb-placeholder"><i className="uil uil-image" /></span>
                          )}
                          <span className="dashboard-home-analytics-list-name">{p.productName || p.productArName || p._id}</span>
                          <span className="dashboard-home-analytics-list-count">{p.count} views</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div className="dashboard-home-analytics-card">
                  <h3 className="dashboard-home-analytics-card-title">
                    <i className="uil uil-apps" />
                    Top Categories
                  </h3>
                  <ul className="dashboard-home-analytics-list">
                    {(analytics.topCategories || []).length === 0 ? (
                      <li className="dashboard-home-analytics-empty">No data yet</li>
                    ) : (
                      (analytics.topCategories || []).map((c) => (
                        <li key={c._id}>
                          <span className="dashboard-home-analytics-list-name">{c.categoryName || c._id}</span>
                          <span className="dashboard-home-analytics-list-count">{c.count} visits</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </div>

              {/* Traffic Sources bar */}
              {(analytics.trafficSources || []).length > 0 && (
                <div className="dashboard-home-traffic-section">
                  <h3 className="dashboard-home-analytics-card-title" style={{ marginBottom: 16 }}>
                    <i className="uil uil-arrow-growth" />
                    Traffic Sources
                  </h3>
                  <div className="dashboard-home-traffic-grid">
                    {(analytics.trafficSources || []).map((s) => {
                      const total = (analytics.trafficSources || []).reduce((a, b) => a + b.count, 0);
                      const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
                      const meta = TRAFFIC_SOURCE_META[s._id] || TRAFFIC_SOURCE_META.other;
                      return (
                        <div key={s._id} className="dashboard-home-traffic-row">
                          <span className="dashboard-home-traffic-icon" style={{ color: meta.color }}><i className={meta.icon} /></span>
                          <span className="dashboard-home-traffic-name">{meta.label}</span>
                          <div className="dashboard-home-traffic-bar-wrap">
                            <div className="dashboard-home-traffic-bar" style={{ width: `${pct}%`, background: meta.color }} />
                          </div>
                          <span className="dashboard-home-traffic-count">{s.count}</span>
                          <span className="dashboard-home-traffic-pct">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </section>

        {/* Recent Activity */}
        <section className="dashboard-home-activity-section">
          <h2 className="dashboard-home-section-title">Recent Activity</h2>
          {!dashStats ? (
            <div className="dashboard-home-activity-loading">Loading…</div>
          ) : (
            <div className="dashboard-home-activity-grid">

              {/* Latest Orders */}
              <div className="dashboard-home-activity-card">
                <h3 className="dashboard-home-activity-card-title">
                  <i className="uil uil-shopping-cart" />
                  Latest Orders
                </h3>
                <ul className="dashboard-home-activity-list">
                  {dashStats.recentOrders.length === 0 ? (
                    <li className="dashboard-home-activity-empty">No orders yet</li>
                  ) : (
                    dashStats.recentOrders.map((order) => (
                      <li key={order._id}>
                        <Link to={`/dashboard/orders/${order._id}`}>
                          <div className="dash-activity-row">
                            <div className="dash-activity-row__left">
                              <span className="dashboard-home-activity-text">
                                #{String(order._id).slice(-6).toUpperCase()} · {order.customer?.fullName || "Customer"}
                              </span>
                              <span className="dashboard-home-activity-time">{formatRelativeTime(order.createdAt)}</span>
                            </div>
                            <OrderStatusBadge status={order.status} />
                          </div>
                        </Link>
                      </li>
                    ))
                  )}
                </ul>
                <Link to="/dashboard/orders" className="dashboard-home-activity-view-all">
                  View all orders →
                </Link>
              </div>

              {/* Latest Messages */}
              <div className="dashboard-home-activity-card">
                <h3 className="dashboard-home-activity-card-title">
                  <i className="uil uil-message" />
                  Latest Messages
                </h3>
                <ul className="dashboard-home-activity-list">
                  {dashStats.recentMessages.length === 0 ? (
                    <li className="dashboard-home-activity-empty">No messages yet</li>
                  ) : (
                    dashStats.recentMessages.map((msg) => (
                      <li key={msg._id}>
                        <Link to="/dashboard/messages">
                          <div className="dash-activity-row">
                            <div className="dash-msg-avatar">{(msg.name || "?").charAt(0).toUpperCase()}</div>
                            <div className="dash-activity-row__left">
                              <span className="dashboard-home-activity-text dash-activity-sender">{msg.name}</span>
                              <span className="dashboard-home-activity-time">
                                {(msg.message || "").slice(0, 45)}{(msg.message || "").length > 45 ? "…" : ""}
                              </span>
                            </div>
                            {!msg.isRead && <span className="dash-unread-dot" />}
                          </div>
                        </Link>
                      </li>
                    ))
                  )}
                </ul>
                <Link to="/dashboard/messages" className="dashboard-home-activity-view-all">
                  View all messages →
                </Link>
              </div>

              {/* Recently Added Products */}
              <div className="dashboard-home-activity-card">
                <h3 className="dashboard-home-activity-card-title">
                  <i className="uil uil-package" />
                  Recently Added Products
                </h3>
                <ul className="dashboard-home-activity-list">
                  {dashStats.recentProducts.length === 0 ? (
                    <li className="dashboard-home-activity-empty">No products yet</li>
                  ) : (
                    dashStats.recentProducts.map((product) => (
                      <li key={product._id}>
                        <Link to={`/dashboard/store/edit-product/${product._id}`}>
                          <span className="dashboard-home-activity-text">{product.name || product.nameAr}</span>
                          <span className="dashboard-home-activity-time">{formatRelativeTime(product.createdAt)}</span>
                        </Link>
                      </li>
                    ))
                  )}
                </ul>
                <Link to="/dashboard/store/products" className="dashboard-home-activity-view-all">
                  View all products →
                </Link>
              </div>
            </div>
          )}
        </section>

        {/* Quick Links */}
        <section className="dashboard-home-links-section">
          <h2 className="dashboard-home-section-title">Quick Links</h2>
          <div className="dashboard-home-cards-grid">
            {QUICK_LINK_SECTIONS.map((section) => (
              <div key={section.title} className="dashboard-home-card-group">
                <div className="dashboard-home-card-group-header">
                  <div className="dashboard-home-card-group-title-wrap">
                    {section.icon && (
                      <span className="dashboard-home-card-group-logo">
                        <i className={section.icon} />
                      </span>
                    )}
                    <h3 className="dashboard-home-card-group-title">{section.title}</h3>
                  </div>
                </div>
                <div className="dashboard-home-card-group-links">
                  {section.links.map((link) => (
                    <Link key={link.to} to={link.to} className="dashboard-home-card">
                      <i className={link.icon} />
                      <span>{link.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

      </div>
    </main>
  );
}

export default DashboardHome;
